type VersionPayload = {
  version?: string
}

type StartVersionReloadWatcherOptions = {
  localVersion: string
  message: string
  reloadLinkText: string
  endpoint?: string
  pollIntervalMs?: number
  reloadDelayMs?: number
}

const VERSION_BANNER_ID = 'app-version-reload-banner'

const showReloadBanner = (message: string, reloadLinkText: string, reloadDelayMs: number): void => {
  if (document.getElementById(VERSION_BANNER_ID)) {
    return
  }

  const banner = document.createElement('div')
  banner.id = VERSION_BANNER_ID
  Object.assign(banner.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    zIndex: '99999',
    padding: '12px 16px',
    background: '#7c3aed',
    color: '#ffffff',
    fontWeight: '600',
    textAlign: 'center',
    fontFamily: 'Plus Jakarta Sans, sans-serif'
  })

  const text = document.createTextNode(`${message} `)
  banner.appendChild(text)

  const link = document.createElement('a')
  link.href = '#'
  link.textContent = reloadLinkText
  link.style.color = '#ffffff'
  link.style.textDecoration = 'underline'
  link.style.cursor = 'pointer'

  const timeoutId = window.setTimeout(() => {
    window.location.reload()
  }, reloadDelayMs)

  link.addEventListener('click', (e) => {
    e.preventDefault()
    window.clearTimeout(timeoutId)
    window.location.reload()
  })
  banner.appendChild(link)

  document.body.appendChild(banner)
}

export const startVersionReloadWatcher = ({
  localVersion,
  message,
  reloadLinkText,
  endpoint = '/version.json',
  pollIntervalMs = 60000,
  reloadDelayMs = 60000
}: StartVersionReloadWatcherOptions): (() => void) => {
  let hasShownBanner = false

  const triggerReload = (): void => {
    if (hasShownBanner) {
      return
    }

    hasShownBanner = true
    showReloadBanner(message, reloadLinkText, reloadDelayMs)
  }

  const checkVersion = async (): Promise<void> => {
    try {
      const response = await fetch(`${endpoint}?t=${Date.now()}`, {
        cache: 'no-store'
      })

      if (!response.ok) {
        return
      }

      const payload = (await response.json()) as VersionPayload
      const remoteVersion = payload.version
      if (remoteVersion && remoteVersion !== localVersion) {
        triggerReload()
      }
    } catch {
      // Intentionally swallow network/transient errors for periodic background checks.
    }
  }

  const intervalId = window.setInterval(() => {
    void checkVersion()
  }, pollIntervalMs)

  const onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      void checkVersion()
    }
  }

  document.addEventListener('visibilitychange', onVisibilityChange)
  void checkVersion()

  return () => {
    window.clearInterval(intervalId)
    document.removeEventListener('visibilitychange', onVisibilityChange)
  }
}
